import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Checkbox, Typography, IconButton, Switch, useMediaQuery, FormControlLabel } from '@mui/material';
import { PageLayout } from '@ui/components/PageLayout';
import { useHelpTopic } from '@ui/contexts';
import Grid from '@mui/material/Grid2';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import ImageUpload from '@ui/pages/Users/AddEditUser/components/ImageUpload';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { HDateRangePicker } from '@ui/components/common/Pickers';
import { PhoneInputMasking } from '@ui/components/PhoneMasking/PhoneInputMasking';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useEffect, useMemo } from 'react';
import { useAppDispatch } from '@application/store/useStore';
import { getAllUserCompaniesAsync } from '@application/features/company/companyThunk';
import { useSelector } from 'react-redux';
import { getAllInterestsWithoutPaginationAsync } from '@application/features/interests/interestsThunk';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { toast } from 'react-toastify';
import { createPartnerAsync, getPartnerByIdAsync, updatePartnerAsync } from '../../../../application/features/partner/partnerThunk';
import { useLocation, useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import FormValidationMessage from '@ui/components/FormValidationMessage';
import { hasPermission } from '@application/helpers/permission-helper';
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || 'Other';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(company.value);
    });
    return regionGroups;
};
const NewSponsorship = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isEdit = searchParams.get('edit') === '1';
    useHelpTopic('newPartner');
    const partnerId = searchParams.get('id');
    const theme = useTheme();
    const isBelow768 = useMediaQuery('(max-width:768px)');
    const { control, setValue, handleSubmit, reset, formState: { errors }, getValues } = useForm({
        defaultValues: {
            sponsorshipLogo: null,
            fullName: '',
            partnerType: 0,
            interests: [],
            sponsorshipDuration: { startDate: '', endDate: '' },
            sponsorshipDescription: '',
            partnerContactPersons: [
                {
                    id: '',
                    firstName: '',
                    lastName: '',
                    phoneNo: '',
                    email: '',
                    isPrimary: true,
                    profilePicture: null
                }
            ],
            companyIds: [],
            tags: []
        }
    });
    const { companies, companiesByRegion, selectedCompanyId } = useSelector((state) => state.company);
    const { interestswithoutpagination } = useSelector((state) => state.interests);
    const { partner } = useSelector((state) => state.partner);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const dispatch = useAppDispatch();
    const isBelow1300 = useMediaQuery('(max-width:1299.95px)');
    const isbetween1024and1280 = useMediaQuery('(min-width:1024px) and (max-width:1280px)');
    const canCreatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Create');
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    useEffect(() => {
        // Check permissions on mount
        if (isEdit && !canUpdatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to edit partners');
            navigate('/sponsorships');
            return;
        }
        if (!isEdit && !canCreatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to create partners');
            navigate('/sponsorships');
            return;
        }
    }, [isEdit, canCreatePartners, canUpdatePartners, navigate, t]);
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            dispatch(getAllUserCompaniesAsync());
        }
        dispatch(getAllInterestsWithoutPaginationAsync());
    }, [dispatch, selectedCompanyId]);
    useEffect(() => {
        if (isEdit && partnerId) {
            void dispatch(getPartnerByIdAsync(partnerId));
        }
    }, [dispatch, isEdit, partnerId]);
    const tagOptions = [
        { label: 'Football', value: 'football' },
        { label: 'PEC Zwolle', value: 'pec_zwolle' },
        { label: 'Outdoor activity', value: 'outdoor_activity' }
    ];
    const companyRegionLookup = useMemo(() => {
        const lookup = {};
        companiesByRegion.forEach((region) => {
            region.companies.forEach((company) => {
                lookup[company.id] = region.regionLabel || 'Other';
            });
        });
        return lookup;
    }, [companiesByRegion]);
    const companyOptions = useMemo(() => companies.map((company) => ({
        label: company.name,
        value: company.id,
        regionLabel: companyRegionLookup[company.id] || 'Other'
    })), [companies, companyRegionLookup]);
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    const interestOptions = useMemo(() => (interestswithoutpagination ?? []).map((interest) => ({
        label: interest.title,
        value: interest.id
    })), [interestswithoutpagination]);
    const formatDate = (date) => new Date(date).toISOString();
    const asFileOrUndefined = (value) => {
        return typeof value === 'string' ? undefined : (value ?? undefined);
    };
    const { fields: contactPersonsFields, append: appendContact, remove: removeContact, replace: replaceContacts } = useFieldArray({
        control,
        name: 'partnerContactPersons'
    });
    const isAllowedImageUpload = (file) => {
        // If MIME is present, it must be an image
        if (file.type && !file.type.toLowerCase().startsWith('image/'))
            return false;
        const allowed = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        const ext = file.name?.split('.').pop()?.toLowerCase() ?? '';
        return !!ext && allowed.includes(ext);
    };
    const onSubmit = async (data) => {
        // Permission check as safety measure
        if (isEdit && !canUpdatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to update partners');
            return;
        }
        if (!isEdit && !canCreatePartners) {
            toast.error(t('common.unauthorized') || 'You do not have permission to create partners');
            return;
        }
        // Final safety net: block any non-image files from being sent in FormData
        if (data.sponsorshipLogo instanceof File && !isAllowedImageUpload(data.sponsorshipLogo)) {
            toast.error(t('users.fileTypeError'));
            return;
        }
        const invalidContactPhoto = (data.partnerContactPersons || []).some((p) => p.profilePicture instanceof File && !isAllowedImageUpload(p.profilePicture));
        if (invalidContactPhoto) {
            toast.error(t('users.fileTypeError'));
            return;
        }
        const primaryCount = (data.partnerContactPersons || []).filter((p) => !!p.isPrimary).length;
        if (primaryCount !== 1) {
            toast.error(t('sponsorships.selectExactlyOnePrimaryContactPerson'));
            return;
        }
        // Validate sponsorship duration is provided
        if (!data.sponsorshipDuration ||
            !data.sponsorshipDuration.startDate ||
            !data.sponsorshipDuration.endDate ||
            data.sponsorshipDuration.startDate === '' ||
            data.sponsorshipDuration.endDate === '') {
            toast.error(t('validations.required', { field: t('sponsorships.sponsorshipDuration') }));
            return;
        }
        if (isEdit && partnerId) {
            const updateData = {
                id: partnerId,
                fullName: data.fullName.trim(),
                description: data.sponsorshipDescription,
                type: data.partnerType,
                partnershipStartDate: formatDate(data.sponsorshipDuration.startDate),
                partnershipEndDate: formatDate(data.sponsorshipDuration.endDate),
                logo: asFileOrUndefined(data.sponsorshipLogo),
                companyIds: data.companyIds?.map((c) => c.value) || [],
                partnerInterestIds: data.interests?.map((i) => i.value) || [],
                partnerContactPersons: (data.partnerContactPersons || []).map((p) => ({
                    id: p.id || '',
                    firstName: p.firstName.trim() || '',
                    lastName: p.lastName.trim() || '',
                    name: `${p.firstName} ${p.lastName}`.trim(),
                    phoneNo: p.phoneNo,
                    email: p.email,
                    isPrimary: !!p.isPrimary,
                    profilePicture: asFileOrUndefined(p.profilePicture)
                }))
            };
            await dispatch(updatePartnerAsync(updateData)).unwrap();
            navigate(`/sponsorships`);
            return;
        }
        const createData = {
            fullName: data.fullName.trim(),
            description: data.sponsorshipDescription,
            type: data.partnerType,
            partnershipStartDate: formatDate(data.sponsorshipDuration.startDate),
            partnershipEndDate: formatDate(data.sponsorshipDuration.endDate),
            logo: asFileOrUndefined(data.sponsorshipLogo),
            companyIds: data.companyIds?.map((c) => c.value) || [],
            partnerInterestIds: data.interests?.map((i) => i.value) || [],
            partnerContactPersons: (data.partnerContactPersons || []).map((p) => ({
                firstName: p.firstName.trim() || '',
                lastName: p.lastName.trim() || '',
                name: `${p.firstName} ${p.lastName}`.trim(),
                phoneNo: p.phoneNo,
                email: p.email,
                isPrimary: !!p.isPrimary,
                profilePicture: asFileOrUndefined(p.profilePicture)
            }))
        };
        await dispatch(createPartnerAsync(createData)).unwrap();
        navigate(`/sponsorships`);
        reset({
            fullName: '',
            sponsorshipDescription: '',
            sponsorshipDuration: { startDate: undefined, endDate: undefined },
            sponsorshipLogo: undefined,
            companyIds: [],
            interests: [],
            partnerContactPersons: [
                {
                    id: '',
                    firstName: '',
                    lastName: '',
                    phoneNo: '',
                    email: '',
                    isPrimary: true,
                    profilePicture: null
                }
            ],
            partnerType: 0,
            tags: []
        });
    };
    // Prefill values when partner loads in edit mode
    useEffect(() => {
        if (isEdit && partner) {
            const fallbackFullName = partner.fullName?.trim() ||
                partner.name?.trim() ||
                `${partner.firstName ?? ''} ${partner.lastName ?? ''}`.trim();
            setValue('fullName', fallbackFullName);
            setValue('sponsorshipDescription', partner.description ?? '');
            setValue('sponsorshipLogo', partner.logoUrl ?? null);
            const start = partner.partnershipStartDate
                ? new Date(partner.partnershipStartDate).toISOString()
                : '';
            const end = partner.partnershipEndDate
                ? new Date(partner.partnershipEndDate).toISOString()
                : '';
            setValue('sponsorshipDuration', { startDate: start || '', endDate: end || '' });
            const partnerCompanyIds = partner.companyIds || [];
            const selectedCompanies = companyOptions
                .filter((opt) => partnerCompanyIds?.includes(opt.value))
                .map((opt) => ({ label: opt.label, value: opt.value })) || [];
            setValue('companyIds', selectedCompanies);
            const partnerInterestIds = partner.interestIds || [];
            const selectedInterests = interestOptions
                .filter((opt) => partnerInterestIds?.includes(opt.value))
                .map((opt) => ({ label: opt.label, value: opt.value })) || [];
            setValue('interests', selectedInterests);
            let mappedContacts = (partner.contactPersons || []).map((c) => ({
                id: c.id,
                firstName: c.firstName ?? '',
                lastName: c.lastName ?? '',
                phoneNo: c.phoneNo ?? '',
                email: c.email ?? '',
                isPrimary: !!c?.isPrimary,
                profilePicture: c.profilePictureUrl ?? null
            }));
            if (mappedContacts.length === 0) {
                mappedContacts = [
                    {
                        id: '',
                        firstName: '',
                        lastName: '',
                        phoneNo: '',
                        email: '',
                        isPrimary: true,
                        profilePicture: ''
                    }
                ];
            }
            else {
                const primaryCountPrefill = mappedContacts.filter((p) => p.isPrimary).length;
                if (primaryCountPrefill !== 1) {
                    mappedContacts = mappedContacts.map((p, i) => ({ ...p, isPrimary: i === 0 }));
                }
            }
            replaceContacts(mappedContacts);
        }
    }, [isEdit, partner, companies, interestOptions, setValue, replaceContacts]);
    return (_jsx(PageLayout, { title: isEdit ? t('sponsorships.editSponsorship') : t('sponsorships.newSponsorship'), children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsxs(Box, { display: 'flex', flexDirection: 'column', children: [_jsx(Box
                    // maxHeight={
                    //   isBelow1300
                    //     ? isbetween1024and1280
                    //       ? 'calc(100dvh - 210px)'
                    //       : 'calc(100dvh - 170px)'
                    //     : 'calc(100dvh - 180px)'
                    // }
                    , { 
                        // maxHeight={
                        //   isBelow1300
                        //     ? isbetween1024and1280
                        //       ? 'calc(100dvh - 210px)'
                        //       : 'calc(100dvh - 170px)'
                        //     : 'calc(100dvh - 180px)'
                        // }
                        display: 'flex', sx: { pr: 0.5 }, children: _jsxs(Grid, { container: true, spacing: '12px', gap: '12px', children: [_jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Grid, { size: { xs: 12 }, mb: 1.5, children: _jsx(Typography, { variant: 'h5', children: t('sponsorships.mainInformation') }) }), _jsx(Box, { p: 2, borderRadius: 1.75, sx: {
                                                backgroundColor: 'transparent',
                                                border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                                                maxHeight: isBelow768 ? undefined : "calc(100dvh - 200px)",
                                                overflowY: 'auto'
                                            }, children: _jsxs(Grid, { container: true, spacing: '12px', gap: '12px', children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(ImageUpload, { centerVariant: false, name: 'sponsorshipLogo', control: control, label: t('sponsorships.uploadLogo') }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledHCheckboxAutocomplete, { name: 'companyIds', control: control, bg: 'grey8', options: companyOptions, label: t('labels.company'), error: errors.companyIds?.message, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: false, selectAllLabel: t('actions.selectAll'), rules: {
                                                                required: t('validations.required', {
                                                                    field: t('labels.company')
                                                                }),
                                                                validate: (value) => {
                                                                    if (!value || value.length === 0) {
                                                                        return t('validations.required', {
                                                                            field: t('labels.company')
                                                                        });
                                                                    }
                                                                    return true;
                                                                }
                                                            }, groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                                                                const regionLabel = params.group;
                                                                const regionCompanyIds = regionGroups[regionLabel] || [];
                                                                const currentCompanies = control._formValues.companyIds || [];
                                                                const currentCompanyIds = currentCompanies.map((c) => typeof c === 'object' && c.value ? c.value : c);
                                                                const allSelected = regionCompanyIds.length > 0 &&
                                                                    regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                                                                const someSelected = !allSelected &&
                                                                    regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                                                                const isUngrouped = !regionLabel;
                                                                return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                                                                        e.stopPropagation();
                                                                                        if (allSelected) {
                                                                                            const updatedSelection = currentCompanies.filter((c) => {
                                                                                                const id = typeof c === 'object' && c.value ? c.value : c;
                                                                                                return !regionCompanyIds.includes(id);
                                                                                            });
                                                                                            control._formValues.companyIds = updatedSelection;
                                                                                            reset({ ...control._formValues });
                                                                                        }
                                                                                        else {
                                                                                            const newCompanies = companyOptions.filter((opt) => regionCompanyIds.includes(opt.value) &&
                                                                                                !currentCompanyIds.includes(opt.value));
                                                                                            const updatedSelection = [
                                                                                                ...currentCompanies,
                                                                                                ...newCompanies
                                                                                            ];
                                                                                            control._formValues.companyIds = updatedSelection;
                                                                                            reset({ ...control._formValues });
                                                                                        }
                                                                                    }, sx: {
                                                                                        '&.Mui-disabled': {
                                                                                            opacity: 0.5
                                                                                        }
                                                                                    } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                                                            } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'fullName', label: t('sponsorships.fullName'), control: control, rules: {
                                                                required: t('validations.required', {
                                                                    field: t('sponsorships.fullName')
                                                                })
                                                            }, errorMessage: errors.fullName?.message }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: 'sponsorshipDuration', control: control, rules: {
                                                                required: t('validations.required', {
                                                                    field: t('sponsorships.sponsorshipDuration')
                                                                }),
                                                                validate: (value) => {
                                                                    if (!value?.startDate ||
                                                                        !value?.endDate ||
                                                                        value.startDate === '' ||
                                                                        value.endDate === '') {
                                                                        return t('validations.required', {
                                                                            field: t('sponsorships.sponsorshipDuration')
                                                                        });
                                                                    }
                                                                    return true;
                                                                }
                                                            }, render: ({ field, fieldState }) => (_jsxs(Box, { children: [_jsx(HDateRangePicker, { bg: 'grey8', label: t('sponsorships.sponsorshipDuration'), value: field.value, handleSelectedRange: (range) => {
                                                                            field.onChange(range || { startDate: '', endDate: '' });
                                                                        } }), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'sponsorshipDescription', label: t('sponsorships.sponsorshipDescription'), control: control, multiline: true, rows: 4, rules: {
                                                                required: t('validations.required', {
                                                                    field: t('sponsorships.sponsorshipDescription')
                                                                })
                                                            }, errorMessage: errors.sponsorshipDescription?.message }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: _jsx(ControlledHCheckboxAutocomplete, { name: 'interests', control: control, bg: 'grey8', options: interestOptions, label: t('labels.tags'), error: errors?.interests, getOptionKey: (option) => option.value, showSelectAll: true, isSortable: true, placeholder: t('labels.selectTags'), checkboxMode: true, selectAllLabel: t('actions.selectAll'), tagsPosition: 'below' }) }) })] }) })] }), _jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Grid, { size: { xs: 12 }, mb: 1.5, children: _jsx(Typography, { variant: 'h5', children: t('sponsorships.contactInformation') }) }), _jsxs(Box, { sx: {
                                                maxHeight: isBelow768 ? undefined : "calc(100dvh - 200px)",
                                                overflowY: 'auto'
                                            }, children: [contactPersonsFields.map((field, index) => (_jsxs(Box, { mb: 2, position: 'relative', children: [_jsx(Box, { p: 2, borderRadius: 1.75, sx: {
                                                                backgroundColor: 'transparent',
                                                                border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`
                                                            }, children: _jsxs(Grid, { container: true, spacing: '12px', gap: '12px', children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(ImageUpload, { centerVariant: false, name: `partnerContactPersons.${index}.profilePicture`, control: control, label: t('sponsorships.uploadPicture') }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { name: `partnerContactPersons.${index}.isPrimary`, control: control, render: ({ field: switchField }) => (_jsx(FormControlLabel, { control: _jsx(Switch, { checked: !!switchField.value, onChange: (_, checked) => {
                                                                                        const all = [...(getValues('partnerContactPersons') || [])];
                                                                                        all.forEach((p, i) => {
                                                                                            p.isPrimary = i === index ? checked : false;
                                                                                        });
                                                                                        setValue('partnerContactPersons', all, {
                                                                                            shouldDirty: true,
                                                                                            shouldValidate: true
                                                                                        });
                                                                                    } }), label: t('sponsorships.isPrimary') })) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', name: `partnerContactPersons.${index}.firstName`, label: t('sponsorships.contactPersonFirstName'), control: control, rules: {
                                                                                required: t('validations.required', {
                                                                                    field: t('sponsorships.contactPersonFirstName')
                                                                                })
                                                                            }, errorMessage: errors?.partnerContactPersons?.[index]?.firstName?.message }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', name: `partnerContactPersons.${index}.lastName`, label: t('sponsorships.contactPersonLastName'), control: control, rules: {
                                                                                required: t('validations.required', {
                                                                                    field: t('sponsorships.contactPersonLastName')
                                                                                })
                                                                            }, errorMessage: errors?.partnerContactPersons?.[index]?.lastName?.message }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(PhoneInputMasking, { bg: 'grey8', name: `partnerContactPersons.${index}.phoneNo`, label: t('sponsorships.contactPersonPhone'), control: control, error: errors?.partnerContactPersons?.[index]?.phoneNo?.message, rules: {
                                                                                required: t('validations.required', {
                                                                                    field: t('sponsorships.contactPersonPhone')
                                                                                }),
                                                                                validate: (value) => {
                                                                                    const phoneRegex = /^\d{8,}$/;
                                                                                    if (!phoneRegex.test(value || '')) {
                                                                                        return t('validations.invalidPhone', {
                                                                                            field: t('sponsorships.contactPersonPhone')
                                                                                        });
                                                                                    }
                                                                                    return true;
                                                                                }
                                                                            } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', name: `partnerContactPersons.${index}.email`, label: t('sponsorships.contactPersonEmail'), control: control, rules: {
                                                                                required: t('validations.required', {
                                                                                    field: t('sponsorships.contactPersonEmail')
                                                                                }),
                                                                                pattern: {
                                                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                                                    message: t('validations.invalidEmail', {
                                                                                        field: t('sponsorships.contactPersonEmail')
                                                                                    })
                                                                                }
                                                                            }, errorMessage: errors?.partnerContactPersons?.[index]?.email?.message }) })] }) }), index > 0 && (_jsx(IconButton, { size: 'small', onClick: () => removeContact(index), sx: { position: 'absolute', border: 'none', top: 15, right: 15 }, children: _jsx(Icon, { icon: 'eva:close-fill', width: 18, height: 18 }) }))] }, field.id))), _jsx(Box, { display: 'flex', justifyContent: 'flex-end', gap: 1, my: 1, children: _jsx(Button, { variant: 'soft', color: 'primary', onClick: () => appendContact({
                                                            id: '',
                                                            firstName: '',
                                                            lastName: '',
                                                            phoneNo: '',
                                                            email: '',
                                                            isPrimary: false,
                                                            profilePicture: null
                                                        }), children: t('actions.addContactPerson') }) })] })] })] }) }), _jsxs(Box, { display: 'flex', justifyContent: 'flex-end', gap: 1, children: [_jsx(Button, { variant: 'outlined', color: 'primary', size: 'large', onClick: () => {
                                    navigate('/sponsorships');
                                }, children: t('actions.cancel') }), _jsx(Button, { type: 'submit', variant: 'contained', color: 'primary', size: 'large', startIcon: _jsx(Icon, { icon: 'eva:checkmark-fill' }), disabled: isEdit ? !canUpdatePartners : !canCreatePartners, children: isEdit ? t('actions.save') : t('sponsorships.createSponsorship') })] })] }) }) }));
};
export default NewSponsorship;
